import { Injectable, signal, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';

export type Theme = 'eyone' | 'dpp' | 'msas' | 'orange' | 'ci' | 'bourkina' | 'fajma';

// Mapping pays → thème
const COUNTRY_THEME_MAP: Record<string, Theme> = {
  SN: 'eyone', // Sénégal → thème Eyone (thème par défaut)
  CI: 'ci', // Côte d'Ivoire → thème CI
  BF: 'bourkina', // Burkina Faso → thème Bourkina
  GA: 'eyone', // Gabon → thème Eyone (thème par défaut)
  // Autres pays → thème Eyone par défaut (géré dans getThemeForCountry)
};

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly THEME_KEY = 'theme';
  private readonly theme = signal<Theme>('eyone'); // Valeur par défaut

  // Exposer le signal pour que les composants puissent l'observer
  readonly theme$ = this.theme.asReadonly();

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    // Initialiser le thème de manière synchrone si on est dans le navigateur
    if (isPlatformBrowser(this.platformId)) {
      const initialTheme = this.getInitialTheme();
      this.theme.set(initialTheme);

      // Vérifier si on doit forcer le thème Orange (pour les tests)
      const subdomainTheme = this.detectSubdomainTheme();
      if (subdomainTheme && subdomainTheme !== initialTheme) {
        // console.log('🔄 Forcing theme to:', subdomainTheme);
        this.setTheme(subdomainTheme, true);
      } else {
        // Appliquer le thème au démarrage
        this.applyTheme(initialTheme);
      }
    }
  }

  getCurrentTheme(): Theme {
    return this.theme();
  }

  /**
   * Nom du produit affiché selon le thème (ex. footer, landing, navbar).
   * Thème FAJMA → "FA'JMA", sinon "Passeport Médical".
   */
  getProductName(): string {
    return this.theme() === 'fajma' ? "FA'JMA" : 'Passeport Médical';
  }

  setTheme(theme: Theme, persist = true): void {
    this.theme.set(theme);
    if (persist && isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.THEME_KEY, theme);
    }
    this.applyTheme(theme);
  }

  toggleTheme(): void {
    const currentTheme = this.theme();
    const themes: Theme[] = [
      'eyone',
      'dpp',
      'msas',
      'orange',
      'ci',
      'bourkina',
      'fajma',
    ];
    const currentIndex = themes.indexOf(currentTheme);
    const nextIndex = (currentIndex + 1) % themes.length;
    this.setTheme(themes[nextIndex]);
  }

  private applyTheme(theme: Theme): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // console.log('Setting theme to:', theme);

    // Mettre à jour l'attribut data-theme sur l'élément html
    document.documentElement.setAttribute('data-theme', theme);
    // console.log(
    //   'data-theme attribute set to:',
    //   document.documentElement.getAttribute('data-theme')
    // );
  }

  /**
   * Détecte le thème basé sur le sous-domaine
   * @returns Le thème correspondant au sous-domaine, ou null si aucun sous-domaine spécifique
   */
  private detectSubdomainTheme(): Theme | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      // Pour les tests : vérifier le paramètre URL ?testOrange=true
      // const urlParams = new URLSearchParams(window.location.search);
      // if (urlParams.get('testOrange') === 'true') {
      //   return 'orange';
      // }

      const hostname = window.location.hostname.toLowerCase();
      const subdomain = hostname.split('.')[0];

      // Mapping sous-domaine → thème
      // Détecter orange.passeportmedical.app (prod) et preprod-orange.passeportmedical.app (preprod)
      if (subdomain === 'orange' || subdomain === 'preprod-orange') {
        return 'orange';
      }

      // Détecter FAJMA : nouveau domaine patient.fajma.com (prod) ou ancien fajma.passeportmedical.app
      if (hostname === 'patient.fajma.com' || hostname.endsWith('.patient.fajma.com')) {
        return 'fajma';
      }
      if (subdomain === 'fajma' || subdomain === 'preprod-fajma') {
        return 'fajma';
      }

      // Ajouter d'autres mappings si nécessaire
      // if (subdomain === 'ci') {
      //   return 'ci';
      // }

      return null;
    } catch (error) {
      console.error('Error detecting subdomain theme:', error);
      return null;
    }
  }

  /**
   * Vérifie si le thème est verrouillé par sous-domaine
   * @returns true si on est sur un sous-domaine qui verrouille le thème
   */
  isThemeLockedBySubdomain(): boolean {
    return this.detectSubdomainTheme() !== null;
  }

  private getInitialTheme(): Theme {
    const validThemes: Theme[] = [
      'eyone',
      'dpp',
      'msas',
      'orange',
      'ci',
      'bourkina',
      'fajma',
    ];
    const envDefault = environment.theme?.default as Theme | undefined;
    const fallbackTheme =
      envDefault && validThemes.includes(envDefault) ? envDefault : 'eyone';

    // Only run on browser side
    if (!isPlatformBrowser(this.platformId)) {
      // Thème par défaut pour server-side rendering (selon l'environnement de build)
      return fallbackTheme;
    }

    // PRIORITÉ 1: Vérifier le sous-domaine (priorité absolue pour Orange)
    const subdomainTheme = this.detectSubdomainTheme();
    // console.log(
    //   '🔍 [getInitialTheme] Subdomain theme detected:',
    //   subdomainTheme
    // );
    if (subdomainTheme) {
      // console.log(
      //   '✅ [getInitialTheme] Using subdomain theme:',
      //   subdomainTheme
      // );
      // Forcer le thème dans localStorage pour éviter les conflits
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(this.THEME_KEY, subdomainTheme);
        // console.log(
        //   '💾 [getInitialTheme] Theme saved to localStorage:',
        //   subdomainTheme
        // );
      }
      return subdomainTheme;
    }

    // Vérifier si un thème est spécifié dans l'URL
    const urlParams = new URLSearchParams(window.location.search);
    const themeParam = urlParams.get('theme') as Theme;

    if (themeParam && validThemes.includes(themeParam as Theme)) {
      return themeParam as Theme;
    }

    // Priorité build (dpp-preprod, bourkina-prod, etc.) : si l'environnement impose un thème
    // autre qu'eyone, l'utiliser pour que ng serve --configuration dpp-preprod affiche bien DPP
    // même avec un localStorage qui contient encore theme=eyone.
    if (fallbackTheme !== 'eyone') {
      return fallbackTheme;
    }

    // Vérifier le pays sélectionné dans le localStorage
    let countryTheme: Theme | null = null;
    try {
      const savedCountry = localStorage.getItem('defaultCountry');
      if (savedCountry) {
        const country = JSON.parse(savedCountry);
        if (country?.code) {
          countryTheme = this.getThemeForCountry(country.code);
        }
      }
    } catch (error) {
      // Ignore errors
    }

    const savedTheme = localStorage.getItem(this.THEME_KEY) as Theme;

    // Priorité (après URL et config build) : pays > localStorage > eyone
    if (countryTheme && validThemes.includes(countryTheme as Theme)) {
      return countryTheme;
    }
    if (savedTheme && validThemes.includes(savedTheme as Theme)) {
      return savedTheme as Theme;
    }
    return fallbackTheme;
  }

  // Méthode pour forcer un thème (utile pour les tests ou le développement)
  forceTheme(theme: Theme): void {
    this.setTheme(theme, false);
  }

  /**
   * Obtient le thème correspondant à un code pays
   * @param countryCode - Code ISO du pays (ex: 'CI', 'SN')
   * @returns Le thème correspondant au pays, ou 'eyone' par défaut
   */
  getThemeForCountry(countryCode: string): Theme {
    const code = countryCode?.toUpperCase();
    return COUNTRY_THEME_MAP[code] || 'eyone';
  }

  /**
   * Change le thème en fonction du code pays
   * @param countryCode - Code ISO du pays (ex: 'CI', 'SN')
   * @param persist - Si true, sauvegarde dans localStorage (défaut: true)
   */
  changeThemeByCountry(countryCode: string, persist = true): void {
    // Ne pas changer le thème si on est sur un sous-domaine qui verrouille le thème
    if (this.isThemeLockedBySubdomain()) {
      return;
    }

    // Ne pas écraser le thème si le build cible un thème spécifique (dpp-preprod, bourkina-prod, etc.)
    // Sinon le CountryGuard / setCountry() remettent eyone après le chargement initial
    const envDefault = environment.theme?.default as Theme | undefined;
    if (envDefault && envDefault !== 'eyone') {
      return;
    }

    const theme = this.getThemeForCountry(countryCode);
    // console.log(
    //   `Changement de thème pour le pays ${countryCode} vers: ${theme}`
    // );
    this.setTheme(theme, persist);
  }
}
